import { useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useCallback, useEffect, useState } from 'react';
import {
  allPalettesAtom,
  customPalettesAtom,
  calculateDefaultColor,
  ColorPaletteWithComputedFields,
} from '@/store/colorPalettes';
import {
  subscribeToPalettes,
  createPalette as apiCreatePalette,
  updatePalette as apiUpdatePalette,
  deletePalette as apiDeletePalette,
} from '@/services/paletteService';
import { ColorPalette, CreateColorPalette, UpdateColorPalette } from '@shared/schemas/colorPalette';
import { useAuth } from '@/contexts/AuthContext';
import { useAtom as useJotaiAtom } from 'jotai';
import { currentOrganizationAtom } from '@/store/organizationAtoms';

// Atom for caching palettes in localStorage, to reduce Firestore reads for non-critical data
// This can be adjusted based on how fresh the data needs to be.
const localStoragePaletteCacheAtom = atomWithStorage<ColorPaletteWithComputedFields[]>(
    'cachedPalettes',
    [],
    createJSONStorage(() => localStorage)
);

export const usePalettes = () => {
  const { currentUser } = useAuth();
  const [currentOrganization] = useJotaiAtom(currentOrganizationAtom);
  const [allPalettes, setAllPalettes] = useAtom(allPalettesAtom);
  const [customPalettes, setCustomPalettes] = useAtom(customPalettesAtom);
  const [cachedPalettes, setCachedPalettes] = useAtom(localStoragePaletteCacheAtom);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const processPalettes = useCallback((palettes: ColorPalette[]): ColorPaletteWithComputedFields[] => {
    return palettes.map(p => ({
      ...p,
      computedDefaultColor: calculateDefaultColor(p),
    }));
  }, []);

  // Real-time subscription to palettes
  useEffect(() => {
    if (!currentOrganization?.id) {
      console.log('⏳ No organization selected, skipping palette subscription');
      setIsLoading(false);
      return;
    }

    // console.log('🔥 Setting up real-time palette subscription for organization:', currentOrganization.id);
    setIsLoading(true);
    setError(null);

    const unsubscribe = subscribeToPalettes(
      currentOrganization.id,
      (palettes: ColorPalette[]) => {
        // console.log('📋 Processing', palettes.length, 'palettes from real-time update');
        const processed = processPalettes(palettes);
        setAllPalettes(processed);
        setCustomPalettes(processed.filter(p => p.ownerId !== 'system'));
        setCachedPalettes(processed); // Update cache
        setIsLoading(false);
        setError(null);
      },
      (err: Error) => {
        console.error('❌ Real-time palette subscription error:', err);
        setError(err.message || 'Failed to load palettes');
        setIsLoading(false);
      }
    );

    // Cleanup subscription on unmount or organization change
    return () => {
      unsubscribe();
    };
  }, [currentOrganization?.id, processPalettes, setAllPalettes, setCustomPalettes, setCachedPalettes]);

  // Legacy fetchPalettes function for backwards compatibility
  const fetchPalettes = useCallback(async (forceRefresh = false) => {
    console.log('⚠️ fetchPalettes called - this is now handled by real-time subscription');
    // Real-time subscription handles data fetching automatically
    // This function kept for backwards compatibility but does nothing
  }, []);

  const createPalette = async (paletteData: CreateColorPalette) => {
    setIsLoading(true);
    try {
      const newPalette = await apiCreatePalette(paletteData);
      if (newPalette) {
        await fetchPalettes(true); // Refresh list, this will set isLoading to false
        return newPalette;
      }
      setIsLoading(false); // Explicitly set if newPalette is null
      return null;
    } catch (err) {
      console.error('Error creating palette:', err);
      setError(err instanceof Error ? err.message : 'Failed to create palette');
      setIsLoading(false);
      throw err; // Re-throw for form handling
    }
  };

  const updatePalette = async (paletteId: string, paletteData: UpdateColorPalette) => {
    setIsLoading(true);
    try {
      const success = await apiUpdatePalette(paletteId, paletteData);
      if (success) {
        await fetchPalettes(true); // Refresh list
      }
      setIsLoading(false); // Set loading false if not refetching or success is false
      return success;
    } catch (err) {
      console.error('Error updating palette:', err);
      setError(err instanceof Error ? err.message : 'Failed to update palette');
      setIsLoading(false);
      throw err;
    }
  };

  const deletePalette = async (paletteId: string) => {
    setIsLoading(true);
    try {
      const success = await apiDeletePalette(paletteId);
      if (success) {
        await fetchPalettes(true); // Refresh list
      }
      setIsLoading(false);
      return success;
    } catch (err) {
      console.error('Error deleting palette:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete palette');
      setIsLoading(false);
      throw err;
    }
  };

  return {
    allPalettes,
    customPalettes,
    fetchPalettes,
    createPalette,
    updatePalette,
    deletePalette,
    isLoading,
    error,
  };
}; 